import { useTranslation } from 'next-i18next'
// Dependencies.
import styled from 'styled-components'
import { useContext, useEffect, useState, useMemo } from 'react'
import { ValueType } from 'react-select'

import { P } from '../../P'
import { Hyperlink } from '../../Hyperlink'
import { colors } from '../../../utils'
import { UserContext, GroupContext } from '../../../context'
import {
  EventHookInterface,
  EventHost,
  NookEvent,
  Option,
} from '../../../types'
import { Select } from '../../Inputs/Select'
import { FormField } from '../../Inputs/FormField'
import { FollowGroupBtnWithUpdateFollowers } from './FollowGroupBtnWithUpdateFollowers'

// Div.
const Div = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;

  &.readOnly {
    margin-top: 30px;
    margin-bottom: 10px;
  }

  .imageContainer {
    border-radius: 100%;
    height: 30px;
    width: 30px;
    min-width: 30px;
    background: ${colors.grey};
    margin-right: 10px;
    overflow: hidden;

    & img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .hostedBy {
    font-weight: 500;
    & span {
      color: ${colors.veryDarkGray};
    }
  }
  .link {
    text-decoration: underline;
  }

  .optionContainer {
    display: flex;
    align-items: center;
  }

  .selectInput {
    background: #fff;
  }

  .groupHostDetail {
    display: flex;
    column-gap: 16px;
  }
`

const Dropdown = styled.div`
  position: relative;

  button {
    display: flex;
    align-items: center;
    cursor: pointer;
    background: none;
    border: none;
  }

  svg {
    margin-top: 2px;
    margin-left: 10px;
  }
`

// Props.
interface Props {
  eventHook: EventHookInterface
  hostGroupId?: number
  setGroupId?: (id: number) => void
}

// Hosted By.
export const HostedBy: React.FC<Props> = (props) => {
  const {
    eventHook: { value },
  } = props

  if (!value) {
    return null
  } else {
    return <HostedByInner {...props} />
  }
}

const HostedByInner: React.FC<Props> = ({
  hostGroupId,
  eventHook,
  setGroupId,
}) => {
  const { t } = useTranslation('common')

  // User.
  const { currentUser } = useContext(UserContext)

  // Event Hook + Value.
  const {
    isEditable,
    value,
    updateFieldsWithPartial,
    updateShowDropDownCreateGroupModal,
  } = eventHook
  const { hosted_group, host, is_user_host, group_id, id, event_paid_type } =
    value as NookEvent

  const DEFAULT_AVATAR = '/images/avatar.png'
  const GROUP_PREFIX = 'group-'
  const INDIVIDUAL_PREFIX = 'individual-'
  const CREATE_GROUP = 'createGroup'

  // State
  const [dropdownValue, setDropdownValue] = useState<Option>(null)
  const [hostDetail, setHostDetail] = useState<EventHost>({
    image: DEFAULT_AVATAR,
    name: null,
    url: null,
    id: 0,
  })

  useEffect(() => {
    if (hosted_group) {
      if (hosted_group.image) {
        setHostDetail({
          ...hostDetail,
          ...{
            image: hosted_group.image,
            name: hosted_group.name,
            url: hosted_group.url,
            id: hosted_group.id,
          },
        })
      } else {
        setHostDetail({
          ...hostDetail,
          ...{
            name: hosted_group.name,
            url: hosted_group.url,
            id: hosted_group.id,
          },
        })
      }
    } else if (host) {
      if (host.image) {
        setHostDetail({
          ...hostDetail,
          ...{ image: host.image, name: host.name, id: host.id },
        })
      } else {
        setHostDetail({ ...hostDetail, ...{ name: host.name, id: host.id } })
      }
    }
  }, [hosted_group, host])

  const extractGroupId = (value: string): number => {
    if (value && value.startsWith(GROUP_PREFIX)) {
      const groupId = value.substring(GROUP_PREFIX.length)
      return parseInt(groupId)
    }
    return null
  }

  const onOptionChange = (option: ValueType<Option, false>) => {
    const { value } = option as Option

    //Reset group_id if non-group is selected
    if (value.startsWith(GROUP_PREFIX)) {
      const groupId = extractGroupId(value)
      if (groupId) updateFieldsWithPartial({ group_id: groupId })
    } else {
      updateFieldsWithPartial({ group_id: null })
      if (event_paid_type === 1) {
        updateFieldsWithPartial({ price: 0 })
        updateFieldsWithPartial({ event_paid_type: 0 })
      }
    }

    if (value === CREATE_GROUP) {
      updateShowDropDownCreateGroupModal(true)
      const individualOption = options.find((o) =>
        o.value.startsWith(INDIVIDUAL_PREFIX),
      )
      if (individualOption) setDropdownValue(individualOption)
    }
  }

  const options = useMemo(() => {
    const options = []
    if (currentUser) {
      options.push({
        value: `${INDIVIDUAL_PREFIX}${currentUser.id}`,
        label: (
          <div className="optionContainer">
            <div className="imageContainer">
              <img
                src={currentUser.image ? currentUser.image : DEFAULT_AVATAR}
              />
            </div>
            {`${currentUser.full_name} (you)`}
          </div>
        ),
      })

      if (currentUser.managed_groups && currentUser.managed_groups.length > 0) {
        currentUser.managed_groups.forEach((group) => {
          options.push({
            value: `${GROUP_PREFIX}${group.id}`,
            label: (
              <div className="optionContainer">
                <div className="imageContainer">
                  <img src={group.avatar ? group.avatar : DEFAULT_AVATAR} />
                </div>
                {group.name}
              </div>
            ),
          })
        })
      } else {
        options.push({
          value: CREATE_GROUP,
          label: 'Create a group! (setup required)',
        })
      }
    }

    return options
  }, [currentUser])

  useEffect(() => {
    if (options) {
      const groupOption = options.find((o) => o.value.startsWith(GROUP_PREFIX))
      const individualOption = options.find((o) =>
        o.value.startsWith(INDIVIDUAL_PREFIX),
      )
      if (groupOption) {
        setDropdownValue(groupOption)
        const groupId = extractGroupId(groupOption.value)
        if (groupId) updateFieldsWithPartial({ group_id: groupId })
      } else {
        setDropdownValue(individualOption)
      }
    }
  }, [options])

  useEffect(() => {
    if (hostGroupId) {
      const groupOption = options.find(
        (o) => o.value === `${GROUP_PREFIX}${hostGroupId}`,
      )
      if (groupOption) {
        setDropdownValue(groupOption)
        updateFieldsWithPartial({ group_id: hostGroupId })
      }
    }
  }, [hostGroupId])

  useEffect(() => {
    let option = null

    if (group_id) {
      if (setGroupId) {
        setGroupId(group_id)
      }
      option = options.find((o) => o.value === `${GROUP_PREFIX}${group_id}`)
    } else {
      option = options.find((o) => o.value.startsWith(INDIVIDUAL_PREFIX))
    }
    if (option) setDropdownValue(option)
  }, [group_id])

  // ..
  return (
    <div>
      {isEditable ? (
        <Div>
          <FormField
            className="hostByField"
            title={t(`Hosted by`, `Hosted by`)}>
            <div>
              <Select
                instanceId="hostedby"
                onChange={onOptionChange}
                options={options}
                placeholder={t(`Select host`, `Select host`)}
                value={dropdownValue}
                disabled={id ? true : false}
              />
            </div>
          </FormField>
        </Div>
      ) : (
        <Div className="readOnly">
          {/* User Pane. */}
          {/* <div className="imageContainer">
          {hostDetail.image && <img src={hostDetail.image}></img>}
        </div> */}

          {/* Hosted By. */}
          {hostDetail.url ? (
            <div className="groupHostDetail">
              <P className="hostedBy">
                {t(`by`, `by`)}{' '}
                <Hyperlink href={`/pages/${hostDetail.url}`} newTab={true}>
                  <span className="link">{hostDetail.name}</span>
                </Hyperlink>
              </P>
              <FollowGroupBtnWithUpdateFollowers eventHook={eventHook} />
            </div>
          ) : (
            <P className="hostedBy">
              {t(`by`, `by`)}{' '}
              <span>
                {hostDetail.name} {is_user_host && '(you)'}
              </span>
            </P>
          )}
        </Div>
      )}
    </div>
  )
}
